import { useEffect, useState, useCallback } from "react";
import "./Admin.css";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AdminDashboard = () => {
    const [users, setUsers] = useState([]);
    const [withdrawRequests, setWithdrawRequests] = useState([]);
    const [receiptRequests, setReceiptRequests] = useState([]);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedReceipt, setSelectedReceipt] = useState(null);
    const [activeTab, setActiveTab] = useState("users");
    const [loading, setLoading] = useState(false);
    const [editingCoinId, setEditingCoinId] = useState(null);
    const [tempCoinValue, setTempCoinValue] = useState("");
   const [editingCoinsWonId, setEditingCoinsWonId] = useState(null);
    const [tempCoinsWonValue, setTempCoinsWonValue] = useState("");

    const navigate = useNavigate();

    const usersPerPage = 10;

    // Memoized fetch functions
    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) throw new Error("No token found! Please login.");

            const res = await fetch("https://backend.aubattle.com/api/admin/users", {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.message || "Failed to fetch users");
            }

            const data = await res.json();
            setUsers(data);
        } catch (err) {
            console.error("❌ API Error:", err.message);
            setError(err.message);
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    }, []);
  const handleUpdateCoinsWon = async (email) => {
    if (!tempCoinsWonValue || isNaN(tempCoinsWonValue)) {
        toast.error("Please enter a valid number");
        return;
    }

    try {
        const token = localStorage.getItem("token");
        const response = await fetch('https://backend.aubattle.com/api/coins/update-coins-won', {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ 
                email: email, 
                coinsWonToAdd: Number(tempCoinsWonValue) 
            }),
        });

        let data;
        try {
            data = await response.json();
        } catch {
            data = {};
        }

        if (!response.ok) {
            throw new Error(data.message || 'Failed to update coins won');
        }

        toast.success(data.message || 'Coins won updated successfully!');
        
        setEditingCoinsWonId(null);
        setTempCoinsWonValue("");
        fetchUsers();
    } catch (error) {
        console.error('Update coins won error:', error);
        toast.error(error.message || 'Error updating coins won');
    }
};
const startEditingCoinsWon = (id, currentCoinsWon) => {
    setEditingCoinsWonId(id);
    setTempCoinsWonValue(currentCoinsWon);
};

const cancelEditingCoinsWon = () => {
    setEditingCoinsWonId(null);
    setTempCoinsWonValue("");
};


    const fetchWithdrawRequests = useCallback(async () => {
        try {
            const res = await axios.get("https://backend.aubattle.com/api/admin/withdraw", {
                headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
            });

            const pendingRequests = res.data.filter(r => r.status === 'pending');
            setWithdrawRequests(pendingRequests);
        } catch (error) {
            console.error("Error fetching withdraw requests:", error);
            toast.error("Failed to fetch withdrawal requests");
        }
    }, []);

    const fetchReceipts = useCallback(async () => {
        try {
            const res = await fetch("https://backend.aubattle.com/api/admin/receipts", {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });
            if (!res.ok) throw new Error("Failed to fetch receipts");
            setReceiptRequests(await res.json());
        } catch (err) {
            console.error("Error:", err);
            toast.error("Failed to fetch receipts");
        }
    }, []);

    useEffect(() => {
        fetchUsers();
        fetchWithdrawRequests();
        fetchReceipts();
    }, [fetchUsers, fetchWithdrawRequests, fetchReceipts]);

    const handleLogout = () => {
        localStorage.removeItem("token");
        navigate("/");
        toast.info("Logged out successfully");
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this user?")) return;

        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`https://backend.aubattle.com/api/admin/delete-user/${id}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });

            if (!res.ok) throw new Error("Failed to delete user");
            setUsers(users.filter(user => user.id !== id));
            toast.success("User deleted successfully!");
        } catch (err) {
            toast.error(err.message);
        }
    };

    const startEditingCoins = (id, currentCoins) => {
        setEditingCoinId(id);
        setTempCoinValue(currentCoins);
    };

    const cancelEditingCoins = () => {
        setEditingCoinId(null);
        setTempCoinValue("");
    };

    const handleUpdateCoins = async (id) => {
        if (!tempCoinValue || isNaN(tempCoinValue)) {
            toast.error("Please enter a valid number");
            return;
        }

        try {
            const token = localStorage.getItem("token");
            const res = await fetch("https://backend.aubattle.com/api/admin/update-coins", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ user_id: id, amount: tempCoinValue }),
            });

            if (!res.ok) throw new Error("Failed to update coins!");
            
            setUsers(users.map(user => user.id === id ? { ...user, coins: tempCoinValue } : user));
            setEditingCoinId(null);
            setTempCoinValue("");
            toast.success("Coins updated successfully!");
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleWithdrawAction = async (withdrawId, action) => {
        try {
            await axios.post("https://backend.aubattle.com/api/admin/withdraw-action", 
                { withdrawId, action },
                { headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } }
            );

            setWithdrawRequests(withdrawRequests.filter(request => request.id !== withdrawId));
            toast.success(`Withdrawal request ${action}d successfully!`);
        } catch (error) {
            console.error("Error updating withdrawal:", error);
            toast.error("Failed to update withdrawal request");
        }
    };
    

    const handleReceiptAction = async (receipt_id, action) => {
        try {
            const endpoint = action === "approve" 
                ? "https://backend.aubattle.com/api/admin/approve-receipt" 
                : "https://backend.aubattle.com/api/admin/reject-receipt";

            const res = await fetch(endpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
                body: JSON.stringify({ receipt_id }),
            });

            if (!res.ok) throw new Error(`Failed to ${action} receipt!`);
            setReceiptRequests(receiptRequests.filter((r) => r.id !== receipt_id));
            toast.success(`Receipt ${action}d successfully!`);
        } catch (err) {
            toast.error(err.message);
        }
    };

    const openModal = (image) => {
        setSelectedReceipt(image);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setSelectedReceipt(null);
    };

    const filteredUsers = users.filter(user =>
        user.name?.toLowerCase().includes(search.toLowerCase()) ||
        user.email?.toLowerCase().includes(search.toLowerCase())
    );

    const paginatedUsers = filteredUsers.slice((page - 1) * usersPerPage, page * usersPerPage);

    const adminLinks = [
        { path: '/adminbanner', label: 'Banner' },
        { path: '/adminupdate', label: 'Updates' },
        { path: '/Coinswon', label: 'Won Coins' },
        { path: '/CreateMatchForm', label: 'Create Match' },
        { path: '/adminmatchlist', label: 'Match List' },
        { path: '/adminmatchplayerpage', label: 'Match Players' },
        { path: '/MatchResultUploader', label: 'Upload Results' },
        { path: '/updatekills', label: 'Update Kills' },
                { path: '/adminhistory', label: 'history' },

    ];

    return (
        <div className="admin-dashboard">
            
            <ToastContainer position="top-right" autoClose={3000} />
            
            <header className="admin-header">
                <div className="header-content">
                    <h1>Admin Dashboard</h1>
                    <div className="header-actions">
                        <button className="logout-btn" onClick={handleLogout}>
                            <i className="fas fa-sign-out-alt"></i> Logout
                        </button>
                    </div>
                </div>
            </header>

            <div className="admin-container">
                <aside className="admin-sidebar">
                    <nav className="admin-navigation">
                        {adminLinks.map((link, index) => (
                            <button 
                                key={index}
                                className="nav-btn"
                                onClick={() => navigate(link.path)}
                            >
                                <i className={`fas fa-${getIconForLabel(link.label)}`}></i>
                                {link.label}
                            </button>
                        ))}
                    </nav>
                </aside>

                <main className="admin-main-content">
                    <div className="admin-tabs">
                        <button 
                            className={activeTab === "users" ? "active" : ""}
                            onClick={() => setActiveTab("users")}
                        >
                            <i className="fas fa-users"></i> Users ({users.length})
                        </button>
                        <button 
                            className={activeTab === "withdrawals" ? "active" : ""}
                            onClick={() => setActiveTab("withdrawals")}
                        >
                            <i className="fas fa-money-bill-wave"></i> Withdrawals ({withdrawRequests.length})
                        </button>
                        <button 
                            className={activeTab === "receipts" ? "active" : ""}
                            onClick={() => setActiveTab("receipts")}
                        >
                            <i className="fas fa-receipt"></i> Receipts ({receiptRequests.length})
                        </button>
                    </div>

                    {loading && (
                        <div className="loading-overlay">
                            <div className="spinner"></div>
                            <p>Loading data...</p>
                        </div>
                    )}
                    
                    {activeTab === "users" && (
                        <div className="dashboard-section">
                            <div className="section-header">
                                <h2>User Management</h2>
                                <div className="search-container">
                                    <input
                                        type="text"
                                        placeholder="Search users..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="search-bar"
                                    />
                                    <i className="fas fa-search search-icon"></i>
                                </div>
                            </div>

                            {error ? (
                                <div className="error-message">
                                    <i className="fas fa-exclamation-circle"></i> {error}
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="admin-table">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Name</th>
                                                <th>Email</th>
                                                <th>Coins</th>
                                                <th>Phone</th>
                                                <th>Actions</th>
                                                <th>Coins Won</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paginatedUsers.map((user) => (
                                                <tr key={user.id}>
                                                    <td>{user.id}</td>
                                                    <td>{user.name || "N/A"}</td>
                                                    <td>{user.email || "N/A"}</td>
                                                    
                                                    <td>
                                                        {editingCoinId === user.id ? (
                                                            <div className="coin-edit-container">
                                                                <input
                                                                    type="number"
                                                                    value={tempCoinValue}
                                                                    onChange={(e) => setTempCoinValue(e.target.value)}
                                                                    className="coin-input"
                                                                    autoFocus
                                                                />
                                                                <button 
                                                                    className="save-btn"
                                                                    onClick={() => handleUpdateCoins(user.id)}
                                                                >
                                                                    <i className="fas fa-check"></i>Ok
                                                                </button>
                                                                <button 
                                                                    className="cancel-btn"
                                                                    onClick={cancelEditingCoins}
                                                                >
                                                                    <i className="fas fa-times"></i>Cancel
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <div className="coin-display-container">
                                                                <span>{user.coins || 0}</span>
                                                                <button 
                                                                    className="edit-btn"
                                                                    onClick={() => startEditingCoins(user.id, user.coins)}
                                                                >
                                                                    <i className="fas fa-edit"></i>Add
                                                                </button>
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
    {editingCoinsWonId === user.id ? (
        <div className="coin-edit-container">
            <input
                type="number"
                value={tempCoinsWonValue}
                onChange={(e) => setTempCoinsWonValue(e.target.value)}
                className="coin-input"
                autoFocus
            />
            <button 
                className="save-btn"
                onClick={() => handleUpdateCoinsWon(user.email)}
            >
                <i className="fas fa-check"></i> Ok
            </button>
            <button 
                className="cancel-btn"
                onClick={cancelEditingCoinsWon}
            >
                <i className="fas fa-times"></i> Cancel
            </button>
        </div>
    ) : (
        <div className="coin-display-container">
            <span>{user.coins_won || 0}</span>
            <button 
                className="edit-btn"
                onClick={() => startEditingCoinsWon(user.id, user.coins_won)}
            >
                <i className="fas fa-edit"></i> Add
            </button>
        </div>
    )}
</td>

                                                    <td>{user.phone_num || "N/A"}</td>
                                                    <td className="action-buttons">
                                                        <button 
                                                            className="delete-btn"
                                                            onClick={() => handleDelete(user.id)}
                                                            title="Delete User"
                                                        >
                                                            <i className="fas fa-trash-alt"></i>Delete User
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                            
                                        </tbody>
                                    </table>

                                    <div className="pagination">
                                        <button 
                                            onClick={() => setPage(page - 1)} 
                                            disabled={page === 1}
                                        >
                                            <i className="fas fa-chevron-left"></i> Previous
                                        </button>
                                        <span>Page {page} of {Math.ceil(filteredUsers.length / usersPerPage)}</span>
                                        <button 
                                            onClick={() => setPage(page + 1)} 
                                            disabled={page * usersPerPage >= filteredUsers.length}
                                        >
                                            Next <i className="fas fa-chevron-right"></i>
                                        </button>
                                    </div>
                                </div>
                                
                            )}
                            
                        </div>

                    )}

                    {activeTab === "withdrawals" && (
                        <div className="dashboard-section">
                            <div className="section-header">
                                <h2>Pending Withdrawal Requests</h2>
                            </div>
                            
                            {withdrawRequests.length === 0 ? (
                                <div className="empty-state">
                                    <i className="fas fa-money-bill-wave empty-icon"></i>
                                    <p>No pending withdrawal requests</p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="admin-table">
                                        <thead>
                                            <tr>
                                                <th>User ID</th>
                                                <th>Name</th>
                                                <th>Amount</th>
                                                <th>Method</th>
                                                <th>Account Details</th>
                                                <th>Date</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {withdrawRequests.map((request) => (
                                                <tr key={request.id}>
                                                    <td>{request.user_id}</td>
                                                    <td>{request.name}</td>
                                                    <td className="amount-cell">₹{request.amount}</td>
                                                    <td>{request.method}</td>
                                                    <td className="account-details">{request.account_details}</td>
                                                    <td>{new Date(request.created_at).toLocaleString()}</td>
                                                    <td className="action-buttons">
                                                        <button 
                                                            className="approve-btn"
                                                            onClick={() => handleWithdrawAction(request.id, "approve")}
                                                            title="Approve"
                                                        >
                                                            <i className="fas fa-check"></i>
                                                        </button>
                                                        <button 
                                                            className="reject-btn"
                                                            onClick={() => handleWithdrawAction(request.id, "reject")}
                                                            title="Reject"
                                                        >
                                                            <i className="fas fa-times"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === "receipts" && (
                        <div className="dashboard-section">
                            <div className="section-header">
                                <h2>Receipt Upload Requests</h2>
                            </div>
                            
                            {receiptRequests.length === 0 ? (
                                <div className="empty-state">
                                    <i className="fas fa-receipt empty-icon"></i>
                                    <p>No pending receipt requests</p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="admin-table">
                                        <thead>
                                            <tr>
                                                <th>User</th>
                                                <th>Email</th>
                                                <th>Date</th>
                                                <th>Receipt</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {receiptRequests.map((receipt) => (
                                                <tr key={receipt.id}>
                                                    <td>{receipt.account_name}</td>
                                                    <td>{receipt.gmail}</td>
                                                    <td>{new Date(receipt.created_at).toLocaleString()}</td>
                                                    <td>
                                                        <button 
                                                            className="view-btn"
                                                            onClick={() => openModal(receipt.receipt_image)}
                                                        >
                                                            <i className="fas fa-eye"></i> View
                                                        </button>
                                                    </td>
                                                    <td className="action-buttons">
                                                        <button 
                                                            className="approve-btn"
                                                            onClick={() => handleReceiptAction(receipt.id, "approve")}
                                                            title="Approve"
                                                        >
                                                            <i className="fas fa-check"></i>
                                                        </button>
                                                        <button 
                                                            className="reject-btn"
                                                            onClick={() => handleReceiptAction(receipt.id, "reject")}
                                                            title="Reject"
                                                        >
                                                            <i className="fas fa-times"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </main>
            </div>

            {modalOpen && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <span className="close-btn" onClick={closeModal}>
                            <i className="fas fa-times"></i>
                        </span>
                        <h3>Receipt Image</h3>
                        <div className="image-container">
                            <img 
                                src={`https://backend.aubattle.com/uploads/${selectedReceipt}`} 
                                alt="Receipt" 
                                className="receipt-image"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "https://via.placeholder.com/400x600?text=Image+Not+Found";
                                }}
                            />
                        </div>
                        <div className="modal-actions">
                            <button className="close-modal-btn" onClick={closeModal}>
                                <i className="fas fa-times"></i> Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Helper function to get icons for navigation buttons
function getIconForLabel(label) {
    const iconMap = {
        'Banner': 'image',
        'Updates': 'sync-alt',
        'Won Coins': 'coins',
        'Create Match': 'plus-circle',
        'Match List': 'list',
        'Match Players': 'users',
        'Upload Results': 'cloud-upload-alt',
        'Update Kills': 'skull'
    };
    return iconMap[label] || 'link';
}

export default AdminDashboard;